/**************************************** PROJECT MODULES *****************************************/
import React from 'react';

/***************************************** KEY CONSTANTS ******************************************/
// TYPES
type KeyUpCB = (...args: any[]) => any;

type VertArrow = 'UP' | 'DOWN';
type HArrow = 'LEFT' | 'RIGHT';
type ArrowType = VertArrow | HArrow;

type ArrowKeyHandlers = {up: KeyUpCB; down: KeyUpCB; left: KeyUpCB; right: KeyUpCB};
type OptArrowKeyHandlers = Partial<ArrowKeyHandlers>;

type StrNumMap = Record<string, number>;

// Keycodes (event.keyCode). See www.cambiaresearch.com/articles/15/javascript-char-codes-key-codes
const KEY = {
    TAB: 9,
    ENTER: 13,
    SPACE: 32,
    DELETE: 46,

    CTRL: 17,
    ALT: 18,
    SHIFT: 16,
    ESCAPE: 27,

    ARROW: {LEFT: 37, UP: 38, RIGHT: 39, DOWN: 40},
    SLASH: {FORWARD: 191, BACK: 220},

    EQUAL_SIGN: 187,
    DASH: 189,
    COMMA: 188,

    CHAR: {a: 65, g: 71, m: 77, s: 83, z: 90}, // CHAR are example keys
};

/********************************************* EXPORT *********************************************/
/**
 * Gets data from an event, with given default value
 */
export function getUpdatedInputData<T extends string | number>(defaultValue: T, event: any): T {
    let value = defaultValue;
    if (
        event &&
        event.target &&
        (event.target[`value`] || event.target[`value`] === `` || event.target[`value`] === 0)
    ) {
        value = event.target[`value`];
    }
    return value;
}

/*************************************** KEYPRESS DETECTION ***************************************/
/**
 * @return {boolean} true if event was escape key pressed
 */
const isEscKeyEv = (ev: React.KeyboardEvent<any>) =>
    !!(ev && (ev.key === `Escape` || ev.key === `Esc` || ev.keyCode === KEY.ESCAPE));

/**
 * @return {boolean} true if event was an arrow key press
 */
const detectArrowKeyEv = (ev: React.KeyboardEvent<any>) =>
    (ev && Object.keys(KEY.ARROW).find(arrow => KEY.ARROW[arrow] === ev.keyCode)) || null;

/**
 * @return {boolean} true if event was enter key pressed
 */
const isEnterKeyEv = (ev: React.KeyboardEvent<any>) =>
    ev && (ev.key === `Enter` || ev.keyCode === KEY.ENTER);

/**
 * @return {boolean} true if event was space key pressed
 */
const isSpaceKeyEv = (ev: React.KeyboardEvent<any>) =>
    ev && (ev.key === `Space` || ev.keyCode === KEY.SPACE);

/********************************** RUN CB ON GIVEN KEY DETECTED **********************************/
/**
 * On arrow key pressed, run corresponding function
 * @param {Object} keyHandlers Map each arrow key name (up etc) to fn to run if that key is pressed
 * @param {string} srcNmFn Name of function where onArrowPress is being run (for logging purposes)
 * @return {Function} Runs method in keyHandlers obj w key corresponding to matching arrow key
 * @example onKeyUp={onArrowPress({up: onUpPress, down: onDownPress, right: onClick})}
 */
export const onArrowPress = (keyHandlers: OptArrowKeyHandlers, srcFnNm?: string) => (
    ev: React.KeyboardEvent<any>
) => {

    const arrowKey = detectArrowKeyEv(ev);
    if (!arrowKey) return;

    const keyHandlerFn = keyHandlers[arrowKey.toLowerCase()];
    return keyHandlerFn && keyHandlerFn(ev);
};

/**
 * On enter pressed, run given function
 * Example: onKeyUp={onEnterPress(onClick)}
 * @param {Function} onPressed Run when enter is pressed
 * @return {Function} Runs onPressed if pressed key in event object is `enter`
 */
export const onEnterPress = (onPressed: KeyUpCB) => (event: React.KeyboardEvent<any>) => {
    if (isEnterKeyEv(event)) {
        event.stopPropagation();
        event.preventDefault();
        return onPressed(event);
    }
};

/**
 * On space or enter pressed, run given function
 * Example: onKeyUp={onSubmitKeyPress(onClick || goBack(history, location, match))}
 * @param {Function} onPressed Run when space or enter is pressed
 * @return {Function} Runs onPressed if pressed key in event object is `enter` or `space`
 */
export const onSubmitKeyPress = (onPressed: KeyUpCB) => (event: React.KeyboardEvent<any>) => {
    if (isSpaceKeyEv(event) || isEnterKeyEv(event)) {
        event.stopPropagation();
        event.preventDefault();
        return onPressed(event);
    }
};

/**
 * On escape key pressed, run given function
 * Example: onKeyUp={onEscapePress(onClick)}
 * @param {Function} onPressed Run when escape pressed
 * @return {EventHandler} that runs onPressed if escape key was pressed
 */
export const onEscapePress = (onPressed: KeyUpCB) => (event: React.KeyboardEvent<any>) => {
    if (isEscKeyEv(event)) {
        event.stopPropagation();
        event.preventDefault();
        return onPressed(event);
    }
};
